<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import Banner from '@/components/ds/Banner.vue';
import Button from '@/components/ds/Button.vue';
import ContextHelp from '@/components/ds/ContextHelp.vue';
import EmptyState from '@/components/ds/EmptyState.vue';
import Skeleton from '@/components/ds/Skeleton.vue';
import { useTenantStore } from '@/stores/tenant.js';
import { nextLargerTimeWindow } from '@/utils/timeWindows.js';

const tenant = useTenantStore();

const props = defineProps({
  title: { type: String, required: true },
  helpKey: { type: String, default: null },
  state: {
    type: String,
    required: true,
    validator: (s) => ['loading', 'empty', 'error', 'data'].includes(s)
  },
  emptyHeadline: { type: String, default: '' },
  emptyBody: { type: String, default: '' },
  emptyAction: { type: String, default: null },
  emptyIntegrationGuide: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
  errorDetail: { type: String, default: '' },
  onRetry: { type: Function, default: null },
  refreshStatus: { type: Object, default: null },
  lastUpdatedAt: { type: [Date, String], default: null },
  // Heading level (UX-DR17): default h2 para panels en landing.
  headingLevel: {
    type: Number,
    default: 2,
    validator: (n) => [2, 3, 4].includes(n)
  }
});

const headingTag = computed(() => `h${props.headingLevel}`);
const clock = ref(Date.now());
let clockHandle = null;
onMounted(() => { clockHandle = setInterval(() => { clock.value = Date.now(); }, 30_000); });
onBeforeUnmount(() => { if (clockHandle) clearInterval(clockHandle); });
const largerWindow = computed(() => nextLargerTimeWindow(tenant.timeWindow));
const showLargerWindow = () => {
  if (largerWindow.value) tenant.setTimeWindow(largerWindow.value);
};
const freshnessLabel = computed(() => {
  if (!props.lastUpdatedAt) return null;
  const seconds = Math.max(0, Math.floor((clock.value - new Date(props.lastUpdatedAt).getTime()) / 1000));
  if (seconds < 60) return 'Updated just now';
  const minutes = Math.floor(seconds / 60);
  return `Updated ${minutes}m ago`;
});
</script>

<template>
  <section class="panel ds-card is-padded">
    <header class="panel__header">
      <component
        :is="headingTag"
        class="panel__title"
      >
        <span>{{ title }}</span>
        <ContextHelp
          v-if="helpKey"
          :help-key="helpKey"
        />
      </component>
      <div class="panel__header-extras">
        <span
          v-if="freshnessLabel"
          class="panel__freshness"
          :class="{ 'panel__freshness--stale': refreshStatus?.degraded }"
        >{{ refreshStatus?.stale ? `Data may be outdated · ${freshnessLabel}` : refreshStatus?.degraded ? 'Refresh failed' : refreshStatus?.paused ? 'Updates paused' : refreshStatus?.refreshing ? 'Updating…' : freshnessLabel }}</span>
        <slot name="header" />
      </div>
    </header>
    <div
      class="panel__body"
      aria-live="polite"
    >
      <Skeleton
        v-if="state === 'loading' || refreshStatus?.contextResetting"
        variant="panel"
      />
      <EmptyState
        v-else-if="state === 'empty'"
        :headline="emptyHeadline"
        :body="emptyBody"
      >
        <template #icon>
          <slot name="empty-icon" />
        </template>
        <template
          v-if="(emptyAction === 'larger-window' && largerWindow) || emptyIntegrationGuide"
          #actions
        >
          <Button
            v-if="emptyAction === 'larger-window' && largerWindow"
            variant="secondary"
            size="sm"
            @click="showLargerWindow"
          >
            Show a larger window
          </Button>
          <Button
            v-if="emptyIntegrationGuide"
            as="a"
            href="/guide/integration"
            variant="secondary"
            size="sm"
          >
            Integration guide
          </Button>
        </template>
      </EmptyState>
      <Banner
        v-else-if="state === 'error'"
        tone="danger"
        :title="errorMessage"
      >
        {{ errorDetail }}
        <template
          v-if="onRetry"
          #action
        >
          <Button
            variant="secondary"
            size="sm"
            icon-left="refresh"
            @click="onRetry()"
          >
            Retry
          </Button>
        </template>
      </Banner>
      <div
        v-else
        class="panel__data"
      >
        <Banner
          v-if="refreshStatus?.degraded"
          tone="warning"
          title="Data refresh failed"
          class="panel__refresh-warning"
        >
          Showing the last successful data. {{ freshnessLabel }}.
          <template #action>
            <Button
              variant="secondary"
              size="sm"
              icon-left="refresh"
              @click="onRetry?.()"
            >
              Retry
            </Button>
          </template>
        </Banner>
        <slot />
        <details
          v-if="$slots.tableFallback"
          class="panel__table-fallback"
        >
          <summary class="panel__table-toggle">
            View as table
          </summary>
          <slot name="tableFallback" />
        </details>
      </div>
    </div>
  </section>
</template>

<style scoped>
.panel {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  min-height: 13rem;
}

.panel__header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-3);
}

.panel__title {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-lg);
  font-weight: var(--fw-semibold);
  color: var(--fg-primary);
  letter-spacing: var(--tracking-tight);
  margin: 0;
}

.panel__header-extras {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.panel__freshness {
  font-size: var(--text-xs);
  color: var(--fg-tertiary);
}

.panel__freshness--stale {
  color: var(--warning-text);
}

.panel__refresh-warning {
  margin-bottom: var(--space-3);
}

.panel__body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.panel__data {
  flex: 1;
}

.panel__table-fallback {
  margin-top: var(--space-4);
  font-size: var(--text-sm);
  color: var(--fg-secondary);
}

.panel__table-toggle {
  cursor: pointer;
  font-weight: var(--fw-medium);
  color: var(--fg-secondary);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
}

.panel__table-toggle:hover {
  color: var(--fg-primary);
}
</style>
